import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { type TranslateRequest, type TranslationStatus, translationApi } from '../translation';

export const translationKeys = {
  status: ['translation', 'status'] as const,
  settings: ['translation', 'settings'] as const,
};

export function useTranslationStatusQuery() {
  return useQuery({
    queryKey: translationKeys.status,
    queryFn: () => translationApi.getStatus(),
  });
}

/**
 * Defaults to not-configured while the status is loading, so a consumer (the composer's
 * translate button) never flashes as available on an instance that has no key.
 */
export function useTranslationStatus(): TranslationStatus {
  const { data } = useTranslationStatusQuery();
  return { configured: data?.configured ?? false };
}

export function useTranslationSettingsQuery() {
  return useQuery({
    queryKey: translationKeys.settings,
    queryFn: () => translationApi.getSettings(),
  });
}

function useInvalidateTranslationQueries() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: translationKeys.status });
    qc.invalidateQueries({ queryKey: translationKeys.settings });
  };
}

export function useSetTranslationKeyMutation() {
  const invalidate = useInvalidateTranslationQueries();
  return useMutation({
    mutationFn: (apiKey: string) => translationApi.setKey(apiKey),
    onSuccess: invalidate,
  });
}

export function useClearTranslationKeyMutation() {
  const invalidate = useInvalidateTranslationQueries();
  return useMutation({
    mutationFn: () => translationApi.clearKey(),
    onSuccess: invalidate,
  });
}

export function useSetTranslationEnabledMutation() {
  const invalidate = useInvalidateTranslationQueries();
  return useMutation({
    mutationFn: (enabled: boolean) => translationApi.setEnabled(enabled),
    onSuccess: invalidate,
  });
}

/** No cache to invalidate — a translation is a one-off action, not stored state. */
export function useTranslateMutation() {
  return useMutation({
    mutationFn: (payload: TranslateRequest) => translationApi.translate(payload),
  });
}
